import React, { useEffect, useMemo, useState } from "react";
import styles from "./Collections.module.css";
import { Pagination, Stack } from "@mui/material";
import ProductCard from "../Products/components/ProductCard";
import SelectableProductCard from "./components/SelectableProductCard";
import SkeletonLoading from "../../../GlobalComponents/SkeletonLoading/SkeletonLoading";
import cardStyles from "../Products/components/ProductCard.module.css";
import { AddIcon } from "../../../../utils/Icons";
import { AnimatePresence } from "framer-motion";
import ShareCollectionPopup from "../../../GlobalComponents/ShareCollectionPopup/ShareCollectionPopup";
import DeleteConfirmPopup from "../../../GlobalComponents/DeleteConfirmPopup/DeleteConfirmPopup";
import CompareItemsModal from "./CompareItemsModal/CompareItemsModal";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import backend from "../../../../api";

/**
 * Map a backend snapshot item -> ProductCard-friendly shape
 */
function snapshotToCard(snapshot) {
  const brand = snapshot?.brand || "";
  const source = String(snapshot?.id || "").startsWith("ext:") ? "internet" : "local";
  return {
    id: snapshot?.id || "",
    brand,
    brandShort: brand.slice(0, 10),
    discount: 0,
    name: snapshot?.name || "Product",
    subtitle: snapshot?.subtitle || "",
    category: snapshot?.category || "",
    color: snapshot?.color || "",
    specs: Array.isArray(snapshot?.specs) ? snapshot.specs : [],
    image: snapshot?.image || "/assets/images/placeholder.png",
    price: Number(snapshot?.price || 0),
    available: !!snapshot?.available,
    source,
  };
}

/**
 * Convert API /collections payload -> flat UI list
 * Each UI item keeps (collectionId, itemId) for deletion,
 * and a `product` object for rendering.
 */
function collectionsToUIItems(collections) {
  const flat = [];
  for (const col of collections || []) {
    const collectionId = col?._id;
    const items = Array.isArray(col?.items) ? col.items : [];
    for (const it of items) {
      const itemId = it?._id;
      const snapshot = it?.snapshot || {};
      flat.push({
        key: `${collectionId}:${itemId}`,
        collectionId,
        itemId,
        product: snapshotToCard(snapshot),
      });
    }
  }
  return flat;
}

export default function Collections() {
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;
  const companyId = user?.companyId;

  const [items, setItems] = useState([]); // UIItems: [{ key, collectionId, itemId, product }]
  const [isBootLoading, setIsBootLoading] = useState(true);

  // selection (cap at 3) — use UI key to avoid clashes
  const [selected, setSelected] = useState(new Set());

  // delete confirm popup state
  const [pendingDeleteKey, setPendingDeleteKey] = useState(null);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);

  // share + compare
  const [openSharePopup, setopenSharePopup] = useState(false);
  const [openCompare, setOpenCompare] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // ─────────────────────────────────────────────────────────────
  // Load company-wide collections → flatten
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!companyId) return;
        setIsBootLoading(true);
        const res = await backend.get("/api/collections", { params: { companyId } });
        const cols = Array.isArray(res?.data?.data) ? res.data.data : [];
        const flat = collectionsToUIItems(cols);
        if (!cancelled) {
          setItems(flat);
        }
      } catch (e) {
        console.warn("load collections error:", e?.response?.data || e.message);
      } finally {
        if (!cancelled) setIsBootLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, [companyId]);

  // ─────────────────────────────────────────────────────────────
  // Selection
  // ─────────────────────────────────────────────────────────────
  const showMaxSnack = () => {
    enqueueSnackbar("You can compare up to 3 items.", {
      autoHideDuration: 1600,
      anchorOrigin: { vertical: "top", horizontal: "right" },
      content: (key, message) => (
        <div
          id={String(key)}
          className={styles.maxSnack}
          onClick={() => closeSnackbar(key)}
          role="status"
        >
          {message}
        </div>
      ),
    });
  };

  const toggleSelect = (uiKey) => {
    if (isBootLoading) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uiKey)) {
        next.delete(uiKey);
        return next;
      }
      if (next.size >= 3) {
        showMaxSnack();
        return prev;
      }
      next.add(uiKey);
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Delete (with backend)
  // ─────────────────────────────────────────────────────────────
  const handleOpenConfirmPopup = (uiKey) => {
    setPendingDeleteKey(uiKey);
    setOpenConfirmPopup(true);
  };
  const handleCloseConfirmPopup = () => {
    setOpenConfirmPopup(false);
    setPendingDeleteKey(null);
  };

  const handleConfirmSubmit = async () => {
    try {
      if (!pendingDeleteKey) return;
      const item = items.find((x) => x.key === pendingDeleteKey);
      if (!item) return;

      const { collectionId, itemId } = item;
      await backend.delete(`/api/collection/${collectionId}/items/${itemId}`);

      // update UI
      setItems((arr) => arr.filter((x) => x.key !== pendingDeleteKey));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(pendingDeleteKey);
        return next;
      });
      enqueueSnackbar("Removed from collection", { variant: "success" });
    } catch (e) {
      console.warn("delete collection item error:", e?.response?.data || e.message);
      enqueueSnackbar("Failed to remove", { variant: "error" });
    } finally {
      handleCloseConfirmPopup();
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Compare
  // ─────────────────────────────────────────────────────────────
  const selectedProducts = useMemo(
    () => items.filter((it) => selected.has(it.key)).slice(0, 3).map((it) => it.product),
    [items, selected]
  );
  const openCompareIfValid = () => {
    if (!isBootLoading && selectedProducts.length >= 2) setOpenCompare(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Pagination (client-side)
  // ─────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(items.length / itemsPerPage) || 1;
  const currentItems = useMemo(
    () => items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [items, currentPage]
  );
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(Math.max(totalPages, 1));
  }, [totalPages, currentPage]);

  const onPage = (_, num) => !isBootLoading && setCurrentPage(num);

  const selectedCount = selected.size;
  const SKELETON_COUNT = itemsPerPage;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, items.length);
  const totalItems = items.length;

  return (
    <>
      <div className={`${styles.page} layoutPadding`} aria-busy={isBootLoading}>
        {/* HEADER */}
        {!isBootLoading && items.length === 0 ?
          <div className='empty' >
            <h2 className='emptyTitle' >My Collection </h2>
            <p className="emptyPara" >Save and organize your favorite items for easy access, future use, and side-by-side comparison</p>
            <button
              className={`${styles.btn} ${styles.add}`}
              disabled={isBootLoading}
              onClick={() => navigate("/products")}
            >
              <AddIcon height={13} fill="white" />
              Add Items
            </button>
          </div>
          :
          <div div className={styles.headerRow}>
            <h2 className={styles.title}>My Collection</h2>
            <div
              className={styles.tools}
              style={{
                opacity: isBootLoading ? 0.6 : 1,
                pointerEvents: isBootLoading ? "none" : "auto",
              }}
            >
              <button
                className={`${styles.btn} ${styles.add}`}
                disabled={isBootLoading}
                onClick={() => navigate("/products")}
              >
                <AddIcon height={13} fill="white" />
                Add Items
              </button>

              <button
                className={`${styles.btn} ${styles.compare}`}
                disabled={isBootLoading || selectedCount < 2}
                onClick={openCompareIfValid}
                title={selectedCount < 2 ? "Select at least 2 items" : "Compare items"}
              >
                <img src="/assets/icons/compare.png" height={13} />
                Compare items {selectedCount > 0 ? `(${selectedCount})` : ""}
              </button>

              <button
                className={`${styles.btn} ${styles.share}`}
                disabled={isBootLoading}
                onClick={() => setopenSharePopup(true)}
              >
                <img src="/assets/icons/share.png" height={13} />
                Share collection
              </button>
            </div>
          </div>
        }

        {/* GRID */}
        <div className={styles.grid}>
          {isBootLoading
            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <SelectableProductCard
                key={`sk-${i}`}
                product={null}
                selected={false}
                onToggle={() => { }}
                onDelete={() => { }}
                disabled
              >
                <div className={cardStyles.card}>
                  <div className={cardStyles.imgBox}>
                    <SkeletonLoading
                      length={1}
                      height="100%"
                      width="100%"
                      br="15px"
                      padding="0"
                    />
                  </div>
                  <div className={cardStyles.body}>
                    <div
                      className={cardStyles.titleRow}
                      style={{ alignItems: "center" }}
                    >
                      <SkeletonLoading
                        length={1}
                        height="14px"
                        width="60%"
                        br="6px"
                        padding="0 6px 0 0"
                      />
                    </div>
                    <SkeletonLoading
                      length={1}
                      height="12px"
                      width="80%"
                      br="6px"
                      padding="6px 6px 0 0"
                    />
                    <ul
                      className={cardStyles.specs}
                      style={{ listStyle: "none", padding: "6px 0 0 0" }}
                    >
                      <SkeletonLoading
                        length={4}
                        height="12px"
                        width="90%"
                        br="6px"
                        padding="2px 0"
                      />
                    </ul>
                  </div>
                  <div className={cardStyles.footer}>
                    <SkeletonLoading
                      length={1}
                      height="16px"
                      width="22%"
                      br="8px"
                      padding="0"
                    />
                    <SkeletonLoading
                      length={1}
                      height="28px"
                      width="96px"
                      br="10px"
                      padding="0"
                    />
                  </div>
                </div>
              </SelectableProductCard>
            ))
            : currentItems.map((ui) => (
              <SelectableProductCard
                key={ui.key}
                product={ui.product}
                selected={selected.has(ui.key)}
                onToggle={() => toggleSelect(ui.key)}
                onDelete={() => handleOpenConfirmPopup(ui.key)}
              >
                <ProductCard product={ui.product} />
              </SelectableProductCard>
            ))}
        </div>

        {/* FOOTER */}
        {!isBootLoading && items.length !== 0 &&
          <div className={styles.footerRow}>
            <span className={styles.meta}>
              {isBootLoading
                ? "Loading…"
                : <>Showing {startItem} to {endItem} of {totalItems} results</>}
            </span>
            <Stack
              spacing={2}
              className={styles.pagination}
              style={{
                opacity: isBootLoading ? 0.6 : 1,
                pointerEvents: isBootLoading ? "none" : "auto",
              }}
            >
              <Pagination
                count={totalPages}
                page={Math.min(currentPage, Math.max(totalPages, 1))}
                onChange={onPage}
                shape="rounded"
              />
            </Stack>
          </div>
        }

        <img
          src="/assets/images/hero-wave.png"
          alt=""
          aria-hidden="true"
          className={styles.authBg}
          draggable={false}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </div >

      {/* POPUPS */}
      <AnimatePresence>
        {openSharePopup && (
          <ShareCollectionPopup handleClose={() => setopenSharePopup(false)} />
        )
        }
      </AnimatePresence>

      <AnimatePresence>
        {openConfirmPopup && (
          <DeleteConfirmPopup
            handleClose={handleCloseConfirmPopup}
            handleSubmit={handleConfirmSubmit}
          />
        )}
      </AnimatePresence>

      {/* COMPARE MODAL */}
      <CompareItemsModal
        open={openCompare}
        onClose={() => setOpenCompare(false)}
        products={selectedProducts}
      />
    </>
  );
}
